import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { Plus, Trash2, Calendar, Clock, AlertTriangle, CheckCircle, Circle, ArrowRight, RefreshCw, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

function parseDate(str) {
  if (!str || str === 'Rolling') return null;
  // Try MM/DD/YYYY
  const parts = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (parts) return new Date(parseInt(parts[3]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  // Try YYYY-MM-DD
  const iso = new Date(str);
  return isNaN(iso.getTime()) ? null : iso;
}

function getUrgency(dateStr) {
  if (!dateStr || dateStr === 'Rolling') return { label: 'Rolling', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', ring: 'ring-blue-100', level: 'rolling' };
  const d = parseDate(dateStr);
  if (!d) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { label: 'Passed', color: 'text-gray-400', bg: 'bg-gray-50', border: 'border-gray-200', ring: 'ring-gray-100', level: 'passed', days: diff };
  if (diff <= 7) return { label: `${diff}d left`, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-300', ring: 'ring-red-100', level: 'urgent', days: diff };
  if (diff <= 30) return { label: `${Math.ceil(diff / 7)}w left`, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-300', ring: 'ring-amber-100', level: 'approaching', days: diff };
  return { label: `${Math.ceil(diff / 30)}mo`, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', ring: 'ring-emerald-100', level: 'safe', days: diff };
}

function formatDate(dateStr) {
  if (!dateStr || dateStr === 'Rolling') return 'Rolling';
  const d = parseDate(dateStr);
  if (!d) return dateStr;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function CountdownBar({ urgency }) {
  if (!urgency || urgency.level === 'rolling' || urgency.level === 'passed') return null;
  const maxDays = 120;
  const pct = Math.max(0, Math.min(100, ((maxDays - (urgency.days || 0)) / maxDays) * 100));
  const barColor = urgency.level === 'urgent' ? 'bg-red-500' : urgency.level === 'approaching' ? 'bg-amber-400' : 'bg-emerald-400';
  return (
    <div className="h-1 bg-gray-100 rounded-full overflow-hidden mt-2 w-full">
      <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function DeadlineCard({ deadline }) {
  const urgency = getUrgency(deadline.date);
  const typeColors = {
    'Early Decision': 'bg-purple-50 text-purple-700 border-purple-200',
    'Early Action': 'bg-indigo-50 text-indigo-700 border-indigo-200',
    'Regular Decision': 'bg-slate-50 text-slate-700 border-slate-200',
    'Rolling Admission': 'bg-blue-50 text-blue-700 border-blue-200',
    'deadline': 'bg-gray-50 text-gray-700 border-gray-200',
  };
  const typeBadge = typeColors[deadline.type] || typeColors.deadline;

  return (
    <Card className={`border ${urgency?.border || 'border-[#E2E2E2]'} transition-all hover:shadow-sm`}
      data-testid={`deadline-card-${deadline.school}-${deadline.type}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold truncate">{deadline.school}</h3>
              {deadline.source === 'auto' && (
                <Badge className="bg-[#0F392B]/5 text-[#0F392B] border-[#0F392B]/10 text-[9px]">Auto</Badge>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={`text-[10px] ${typeBadge}`}>{deadline.type}</Badge>
              <span className="text-xs text-muted-foreground">{formatDate(deadline.date)}</span>
            </div>
            <CountdownBar urgency={urgency} />
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            {urgency && (
              <div className={`px-2.5 py-1 rounded-md text-xs font-semibold ${urgency.bg} ${urgency.color} ${urgency.border} border`}>
                {urgency.level === 'urgent' && <AlertTriangle className="h-3 w-3 inline mr-1" />}
                {urgency.label}
              </div>
            )}
            {deadline.url && (
              <a href={deadline.url} target="_blank" rel="noopener noreferrer"
                className="text-[10px] text-[#0F392B] hover:underline flex items-center gap-0.5">
                Apply <ArrowRight className="h-2.5 w-2.5" />
              </a>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TimelinePage() {
  const [deadlines, setDeadlines] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('deadlines');
  const [form, setForm] = useState({ title: '', description: '', event_type: 'task', date: '' });

  const loadAll = useCallback(async () => {
    try {
      const [dl, ev] = await Promise.all([
        api.getDeadlines().catch(() => ({ deadlines: [] })),
        api.getTimeline().catch(() => []),
      ]);
      setDeadlines(dl.deadlines || []);
      setEvents(ev);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadAll();
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      await api.createTimelineEvent(form);
      toast.success('Event added');
      setForm({ title: '', description: '', event_type: 'task', date: '' });
      setOpen(false);
      loadAll();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggle = async (id, completed) => {
    try {
      await api.updateTimelineEvent(id, { completed: !completed });
      loadAll();
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteEvent = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await api.deleteTimelineEvent(id);
      toast.success('Deleted');
      loadAll();
    } catch (err) { toast.error(err.message); }
  };

  // Categorize deadlines by urgency
  const urgent = deadlines.filter(d => { const u = getUrgency(d.date); return u?.level === 'urgent'; });
  const approaching = deadlines.filter(d => { const u = getUrgency(d.date); return u?.level === 'approaching'; });
  const safe = deadlines.filter(d => { const u = getUrgency(d.date); return u?.level === 'safe'; });
  const rolling = deadlines.filter(d => { const u = getUrgency(d.date); return u?.level === 'rolling'; });
  const passed = deadlines.filter(d => { const u = getUrgency(d.date); return u?.level === 'passed'; });

  const now = new Date().toISOString().split('T')[0];
  const upcomingEvents = events.filter(e => e.date >= now && !e.completed);
  const pastEvents = events.filter(e => e.date < now || e.completed);

  if (loading) return (
    <div className="max-w-3xl mx-auto space-y-3 animate-pulse">
      {[1,2,3,4].map(i => <div key={i} className="h-20 bg-[#E2E2E2] rounded-xl" />)}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="timeline-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Deadlines & Timeline
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            {deadlines.length} deadlines from your schools · {events.length} custom events
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={refreshing}
            className="h-8 text-xs border-[#E2E2E2]" data-testid="refresh-deadlines">
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-8 text-xs" data-testid="add-event-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Event
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Add Custom Event</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateEvent} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Title</Label>
                  <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g., Submit Harvard supplement" required data-testid="event-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Type</Label>
                    <Select value={form.event_type} onValueChange={v => setForm({ ...form, event_type: v })}>
                      <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="task">Task</SelectItem>
                        <SelectItem value="deadline">Deadline</SelectItem>
                        <SelectItem value="milestone">Milestone</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Date</Label>
                    <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                      required data-testid="event-date-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Description</Label>
                  <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                    placeholder="Optional..." rows={2} data-testid="event-desc-input" />
                </div>
                <Button type="submit" className="w-full bg-[#0F392B] hover:bg-[#0F392B]/90 text-white" data-testid="event-submit-btn">
                  Add Event
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex gap-1 bg-[#F9F9F7] p-1 rounded-lg" data-testid="timeline-tabs">
        {[
          { id: 'deadlines', label: 'School Deadlines', icon: Calendar },
          { id: 'events', label: 'My Events', icon: Clock },
        ].map(t => (
          <button key={t.id} onClick={() => setView(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-md transition-all flex-1 justify-center
              ${view === t.id ? 'bg-white text-[#0F392B] shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {view === 'deadlines' && (
        <div className="space-y-6">
          {deadlines.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-muted-foreground text-sm">No deadlines yet</p>
              <p className="text-xs text-muted-foreground mt-1">Add schools to "My Schools" and deadlines will auto-populate here</p>
            </div>
          ) : (
            <>
              {/* Summary badges */}
              <div className="flex flex-wrap gap-2" data-testid="deadline-summary">
                {urgent.length > 0 && <Badge className="bg-red-50 text-red-700 border-red-200">{urgent.length} Urgent</Badge>}
                {approaching.length > 0 && <Badge className="bg-amber-50 text-amber-700 border-amber-200">{approaching.length} Approaching</Badge>}
                {safe.length > 0 && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">{safe.length} Safe</Badge>}
                {rolling.length > 0 && <Badge className="bg-blue-50 text-blue-700 border-blue-200">{rolling.length} Rolling</Badge>}
              </div>

              {/* Urgent */}
              {urgent.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <h2 className="text-sm font-semibold text-red-700">Urgent — This Week</h2>
                  </div>
                  <div className="space-y-2">{urgent.map((d, i) => <DeadlineCard key={`u-${i}`} deadline={d} />)}</div>
                </div>
              )}

              {/* Approaching */}
              {approaching.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-amber-400" />
                    <h2 className="text-sm font-semibold text-amber-700">Approaching — This Month</h2>
                  </div>
                  <div className="space-y-2">{approaching.map((d, i) => <DeadlineCard key={`a-${i}`} deadline={d} />)}</div>
                </div>
              )}

              {/* Safe */}
              {safe.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    <h2 className="text-sm font-semibold text-emerald-700">Safe — Plenty of Time</h2>
                  </div>
                  <div className="space-y-2">{safe.map((d, i) => <DeadlineCard key={`s-${i}`} deadline={d} />)}</div>
                </div>
              )}

              {/* Rolling */}
              {rolling.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-blue-400" />
                    <h2 className="text-sm font-semibold text-blue-700">Rolling Admissions</h2>
                  </div>
                  <div className="space-y-2">{rolling.map((d, i) => <DeadlineCard key={`r-${i}`} deadline={d} />)}</div>
                </div>
              )}

              {/* Passed */}
              {passed.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full bg-gray-300" />
                    <h2 className="text-sm font-semibold text-gray-400">Passed</h2>
                  </div>
                  <div className="space-y-2 opacity-50">{passed.map((d, i) => <DeadlineCard key={`p-${i}`} deadline={d} />)}</div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {view === 'events' && (
        <div className="space-y-6">
          {events.length === 0 ? (
            <div className="text-center py-16">
              <Clock className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-muted-foreground text-sm">No custom events yet</p>
              <p className="text-xs text-muted-foreground mt-1">Add tasks, milestones, and personal deadlines</p>
            </div>
          ) : (
            <>
              {upcomingEvents.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Upcoming</h2>
                  <div className="space-y-2">
                    {upcomingEvents.map(ev => (
                      <Card key={ev.id} className="border-[#E2E2E2]" data-testid={`event-card-${ev.id}`}>
                        <CardContent className="p-4 flex items-center gap-3">
                          <button onClick={() => handleToggle(ev.id, ev.completed)} className="flex-shrink-0" data-testid={`toggle-event-${ev.id}`}>
                            <Circle className="h-5 w-5 text-[#0F392B]" strokeWidth={1.5} />
                          </button>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-medium">{ev.title}</h3>
                            {ev.description && <p className="text-xs text-muted-foreground mt-0.5">{ev.description}</p>}
                          </div>
                          <Badge variant="outline" className="text-[10px] capitalize">{ev.event_type}</Badge>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(ev.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                          <button onClick={() => handleDeleteEvent(ev.id, ev.title)}
                            className="text-muted-foreground hover:text-red-500 p-1" data-testid={`delete-event-${ev.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              {pastEvents.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold mb-3 text-muted-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Completed / Past
                  </h2>
                  <div className="space-y-2">
                    {pastEvents.map(ev => (
                      <Card key={ev.id} className="border-[#E2E2E2] opacity-50" data-testid={`event-card-${ev.id}`}>
                        <CardContent className="p-4 flex items-center gap-3">
                          <button onClick={() => handleToggle(ev.id, ev.completed)} data-testid={`toggle-event-${ev.id}`}>
                            <CheckCircle className="h-5 w-5 text-green-500" strokeWidth={1.5} />
                          </button>
                          <div className="flex-1"><h3 className="text-sm font-medium line-through">{ev.title}</h3></div>
                          <span className="text-xs text-muted-foreground">
                            {new Date(ev.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                          <button onClick={() => handleDeleteEvent(ev.id, ev.title)} className="text-muted-foreground hover:text-red-500 p-1">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
